import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Loader2, Sparkles } from "lucide-react";
import api, { setToken, setStoredUser } from "../lib/api";
import { toast } from "sonner";

export default function RegisterDialog({ open, onClose, onRegistered }) {
  // Casa fields
  const [name, setName] = useState("");
  const [familyPwd, setFamilyPwd] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [calendlyUrl, setCalendlyUrl] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  // Admin fields
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPwd, setAdminPwd] = useState("");

  const [loading, setLoading] = useState(false);

  const reset = () => {
    setName(""); setFamilyPwd(""); setWhatsappNumber("");
    setCalendlyUrl(""); setWhatsappMessage("");
    setAdminName(""); setAdminEmail(""); setAdminPwd("");
  };

  const handleClose = () => { if (!loading) { reset(); onClose(); } };

  const submit = async () => {
    if (!name.trim()) return toast.error("Nome Casa obbligatorio");
    if (familyPwd.length < 4) return toast.error("Codice famiglia min 4 caratteri");
    if (!whatsappNumber.trim()) return toast.error("Numero WhatsApp obbligatorio");
    if (!adminName.trim()) return toast.error("Inserisci il tuo nome (admin)");
    if (!adminEmail.trim()) return toast.error("Inserisci la tua email");
    if (adminPwd.length < 8) return toast.error("Password personale min 8 caratteri");

    setLoading(true);
    try {
      // Step 1: create the family
      await api.post("/families", {
        name: name.trim(),
        password: familyPwd,
        whatsapp_number: whatsappNumber.trim(),
        calendly_url: calendlyUrl.trim(),
        whatsapp_message: whatsappMessage.trim(),
      });
      // Step 2: claim admin immediately
      const res = await api.post("/auth/setup-admin", {
        family_password: familyPwd,
        email: adminEmail.trim().toLowerCase(),
        password: adminPwd,
        full_name: adminName.trim(),
      });
      setToken(res.data.token);
      setStoredUser(res.data.user);
      toast.success(`Casa "${name}" creata! Sei l'admin.`);
      reset();
      onRegistered();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore registrazione");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent data-testid="register-dialog" className="max-w-md rounded-3xl border-[#E8E1D9] bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full border border-[#E8E1D9] bg-[#FBF9F6] px-3 py-1">
            <Sparkles className="h-3 w-3 text-[#FF7C43]" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C87941]">Nuova Casa</span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">Crea la tua Casa</DialogTitle>
          <DialogDescription className="text-[#5C5855]">
            Imposta la Casa e il tuo account admin personale.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C87941] pt-1">Dati Casa</p>
          <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Casa Bianchi" className="rounded-xl border-[#E8E1D9]" />
          <Input data-testid="register-family-pwd-input" type="password" value={familyPwd} onChange={(e) => setFamilyPwd(e.target.value)} placeholder="Codice famiglia (min 4)" className="rounded-xl border-[#E8E1D9]" />
          <Input data-testid="register-whatsapp-input" value={whatsappNumber} onChange={(e) => setWhatsappNumber(e.target.value)} placeholder="WhatsApp es. 393273839782" className="rounded-xl border-[#E8E1D9]" />
          <Input data-testid="register-calendly-input" value={calendlyUrl} onChange={(e) => setCalendlyUrl(e.target.value)} placeholder="Calendly URL (opzionale)" className="rounded-xl border-[#E8E1D9]" />
          <Input data-testid="register-message-input" value={whatsappMessage} onChange={(e) => setWhatsappMessage(e.target.value)} placeholder="Messaggio ZAP (opzionale)" className="rounded-xl border-[#E8E1D9]" />

          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#C87941] pt-3">Il tuo account admin</p>
          <Input data-testid="register-admin-name-input" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Il tuo nome" className="rounded-xl border-[#E8E1D9]" />
          <Input data-testid="register-admin-email-input" type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="La tua email" className="rounded-xl border-[#E8E1D9]" />
          <Input data-testid="register-admin-pwd-input" type="password" value={adminPwd} onChange={(e) => setAdminPwd(e.target.value)} placeholder="Password personale (min 8)" className="rounded-xl border-[#E8E1D9]" />
        </div>

        <DialogFooter className="flex flex-row justify-end gap-2">
          <Button type="button" variant="outline" onClick={handleClose} className="rounded-full border-[#E8E1D9]" disabled={loading}>Annulla</Button>
          <Button data-testid="register-submit-button" type="button" onClick={submit} disabled={loading} className="rounded-full bg-[#FF7C43] text-white hover:bg-[#E66A35]">
            {loading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            Crea Casa + Admin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
